import _extends from "@babel/runtime/helpers/extends";
import { PropTypes as AmktCommonPropTypes } from '@atlassian/amkt-common';
import { Map } from 'immutable';
import PropTypes from 'prop-types';
import React, { useMemo } from 'react';
import { Provider } from 'react-redux';
import { DEV, PROD, STG } from '../common/constants/environments';
import CommonPropTypes from '../common/constants/proptypes/CommonPropTypes';
import SearchParamsUpdate, { SearchParamsUpdatePropType } from '../common/records/SearchParamsUpdate';
import configureStore from '../common/store/configureStore';
import { createAlgoliaClient } from '../common/utils/algoliaFactory';
import StaticSearchContainer from './container/StaticSearchContainer';
import reducer from './reducer';
const App = props => {
  const store = useMemo(() => configureStore({
    debug: props.debug,
    reducer
  }), []);
  return /*#__PURE__*/React.createElement(Provider, {
    store: store
  }, /*#__PURE__*/React.createElement(StaticSearchContainer, _extends({}, props, {
    algoliaClient: props.algoliaClient || createAlgoliaClient(props.algoliaConfig.toObject())
  })));
};
App.propTypes = {
  /**
   * Algolia config, ie. Application and API keys
   *
   * shape: Map({
   *      applicationId: string,
   *      apiKey: string
   * })
   *
   * Required if Algolia Client is not provided
   */
  algoliaConfig: AmktCommonPropTypes.requiredIf(CommonPropTypes.algoliaConfig, props => !props.algoliaClient),
  /**
   * Algolia search client for maintaining cache between searches
   * Provided by third-party algoliasearch library, and not dereferenced
   *
   * shape: any (but must have AlgoliaClient class name)
   *
   * Required if Algolia Config is not provided
   */
  algoliaClient: AmktCommonPropTypes.requiredIf(CommonPropTypes.algoliaClient, props => !props.algoliaConfig),
  /**
   * A SearchParamsUpdate where:
   * types: Set<FacetType>
   * apply: (AlgoliaSearchHelper.SearchParameters) => AlgoliaSearchHelper.SearchParameters
   *
   * `types` lets you define a set of facets that aren't already known to Addon Discovery but are defined
   * in Algolia. `apply` lets you apply relevant filtering to the SearchParameters.
   */
  applyOverrides: SearchParamsUpdatePropType,
  /**
   * List<FeaturedContentOverride>
   * For each FeaturedContentOverride, we will always render the node from `render()` in place of the
   * tile at `index`.
   *
   * shape: List.of(
   *      featuredContentOverride : Record({
   *           index: number,
   *           render: function
   *      })
   * )
   */
  contentOverrides: CommonPropTypes.contentOverrides,
  /**
   * A class name to apply to the top-level element.
   */
  containerClassName: PropTypes.string,
  /**
   * When true, will turn on debug mode.
   */
  debug: PropTypes.bool,
  /**
   * Which environment we are running in.
   */
  environment: PropTypes.oneOf([DEV, STG, PROD]).isRequired,
  /**
   * Map<string, string | Set<string>>
   * The filters to apply to the search facets. Each key corresponds to a facet name.
   * Accepts "unenhanced" filters, meaning that for filters that accept
   * multiple values, it will also accept a single value.
   */
  filters: CommonPropTypes.filters,
  /**
   * When true, the "Load more results" button will not be rendered even if there are
   * more possible results to fetch.
   */
  hidePagination: PropTypes.bool,
  /**
   * A map of callbacks for when hit-related actions occur such as click or mount, plus
   * functions for resolving relative URLs in search results into absolute URLs.
   *
   * shape: Map({
   *     hitHrefResolver: function (optional),
   *     hitImageHrefResolver: function,
   *     onHitClick: function,
   *     onHitMount: function (optional),
   *     onRatingsClick: function (optional)
   * })
   */
  hitContext: CommonPropTypes.hitContext.isRequired,
  /**
   * "large" | "small"
   * The size of each hit to render.
   */
  hitSize: CommonPropTypes.hitSize,
  /**
   * If defined, the maximum number of hits to render.
   */
  maxLength: PropTypes.number,
  /**
   * ({ currentPage: number }) => void
   * A callback for when the "Load more results" button is clicked. This overrides the default
   * behaviour of fetching another page of results.
   */
  onLoadMoreResultsClick: PropTypes.func,
  /**
   * Map<string,
   *     ({ params: AlgoliaSearchHelper.SearchParameters, filters: Filters }) => AlgoliaSearchHelper.SearchParameters>
   * With the key being a facet, each function allows low-level modifications to the SearchParameters sent to Algolia.
   */
  resolveAlgoliaQueryFilterOverrides: CommonPropTypes.resolveAlgoliaQueryFilterOverrides,
  /**
   * (List<Hit>) => List<Hit>
   * A function that reorders the given list of hits to a desired order.
   * If provided, only one page of results will ever be displayed.
   */
  sortHits: PropTypes.func
};
App.defaultProps = {
  colSpan: Map(),
  debug: false,
  applyOverrides: SearchParamsUpdate.default_(),
  filters: Map(),
  hidePagination: false,
  screenClass: 'xl'
};
export default App;